import React from "react";
import {
  Dialog as MuiDialog,
  DialogTitle as MuiDialogTitle,
  DialogContent as MuiDialogContent,
  DialogActions as MuiDialogActions,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import { X } from "lucide-react";
import { useTheme } from "@mui/material/styles";

export const Dialog = ({
  open,
  onOpenChange,
  disableBackdropClose = false,
  children,
  ...props
}) => {
  const theme = useTheme();

  // Close the dialog regardless of the reason (including backdrop clicks)
  const handleClose = (event, reason) => {
    if (disableBackdropClose && reason === "backdropClick") return;
    onOpenChange?.(false);
  };

  return (
    <MuiDialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: "12px",
          boxShadow: theme.shadows[4],
          // 400px floor only where it fits; on phones the dialog takes the
          // screen width minus a 16px gutter on each side.
          minWidth: { xs: 0, sm: "400px" },
          [theme.breakpoints.down("sm")]: {
            margin: 2,
            width: "calc(100% - 32px)",
            maxWidth: "calc(100% - 32px)",
            maxHeight: "calc(100% - 32px)",
          },
        },
      }}
      {...props}
    >
      {children}
    </MuiDialog>
  );
};

export const DialogContent = ({ children, className, sx = {}, ...props }) => {
  const theme = useTheme();

  // Manejo de clases CSS personalizadas para compatibilidad
  const parseCustomClasses = (className) => {
    const customStyles = {};

    if (className?.includes("modal-content")) {
      customStyles.padding = "24px";
      customStyles.maxWidth = "600px";
    }
    if (className?.includes("max-w-2xl")) {
      customStyles.maxWidth = "672px";
    }

    return customStyles;
  };

  const customStyles = parseCustomClasses(className);

  return (
    <MuiDialogContent
      sx={{
        padding: "20px 24px",
        ...customStyles,
        ...sx,
      }}
      {...props}
    >
      {children}
    </MuiDialogContent>
  );
};

export const DialogHeader = ({ children, sx = {}, ...props }) => {
  return (
    <Box
      sx={{
        padding: "24px 24px 16px 24px",
        borderBottom: `1px solid #e2e8f0`,
        ...sx,
      }}
      {...props}
    >
      {children}
    </Box>
  );
};

export const DialogTitle = ({ children, className, sx = {}, ...props }) => {
  const theme = useTheme();

  // Manejo de clases CSS personalizadas para compatibilidad
  const parseCustomClasses = (className) => {
    const customStyles = {};

    if (className?.includes("text-xl")) {
      customStyles.fontSize = "1.25rem";
    }

    return customStyles;
  };

  const customStyles = parseCustomClasses(className);

  return (
    <Typography
      variant="h6"
      component="h2"
      sx={{
        fontWeight: 600,
        fontSize: "1.125rem",
        color: theme.palette.text.primary,
        marginBottom: "8px",
        ...customStyles,
        ...sx,
      }}
      {...props}
    >
      {children}
    </Typography>
  );
};

export const DialogDescription = ({
  children,
  className,
  sx = {},
  ...props
}) => {
  const theme = useTheme();

  // Manejo de clases CSS personalizadas para compatibilidad
  const parseCustomClasses = (className) => {
    const customStyles = {};

    if (className?.includes("mt-2")) {
      customStyles.marginTop = "8px";
    }

    return customStyles;
  };

  const customStyles = parseCustomClasses(className);

  return (
    <Typography
      variant="body2"
      sx={{
        color: theme.palette.text.secondary,
        ...customStyles,
        ...sx,
      }}
      {...props}
    >
      {children}
    </Typography>
  );
};

export const DialogActions = ({ children, sx = {}, ...props }) => {
  return (
    <MuiDialogActions
      sx={{
        padding: "16px 24px",
        gap: "8px",
        ...sx,
      }}
      {...props}
    >
      {children}
    </MuiDialogActions>
  );
};
